import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCodigoSesionPosConfigs,
    CodigoSesionPosConfig
} from '../../../services/codigoSesionPosService';
import AddCodigoSesionPos from '../../../components/OrdenSesionPos/AddCodigoSsesionPos';
import EditCodigoSesionPos from '../../../components/OrdenSesionPos/EditCodigoSesionPos';
import DeleteCodigoSesionPos from '../../../components/OrdenSesionPos/DeleteCodigoSesionPos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import Loading from '../../../components/Loading';

const CodigoSesionPosPage: React.FC = () => {
    const [configs, setConfigs] = useState<CodigoSesionPosConfig[]>([]);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<CodigoSesionPosConfig | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteConfig, setDeleteConfig] = useState<CodigoSesionPosConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'codigosesionposconfig' o 'codigo_sesion_pos_config')
    const perm = useModelPermission('codigosesionposconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchCodigoSesionPosConfigs();
                const sortedData = [...data].sort((a, b) => a.id - b.id);
                setConfigs(sortedData);
            } else {
                setConfigs([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return; // esperar a que los permisos se carguen
        loadConfigs();
    }, [perm, loadConfigs]);

    const totalPages = Math.ceil(configs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = configs.slice(startIndex, startIndex + itemsPerPage);

    const handleCreate = () => setShowAddModal(true);

    const handleConfigCreated = (newConfig: CodigoSesionPosConfig) => {
        setConfigs(prev => {
            const updatedConfigs = prev.map(cfg =>
                newConfig.activo ? { ...cfg, activo: false } : cfg
            );
            return [...updatedConfigs, newConfig].sort((a, b) => a.id - b.id);
        });
        setShowAddModal(false);
    };

    const handleConfigUpdated = (updatedConfig: CodigoSesionPosConfig) => {
        setConfigs(prev => {
            return prev.map(cfg => {
                if (cfg.id === updatedConfig.id) {
                    return updatedConfig;
                }
                return updatedConfig.activo ? { ...cfg, activo: false } : cfg;
            });
        });
        setShowEditModal(false);
        setSelectedConfig(null);
    };

    const handleEdit = (cfg: CodigoSesionPosConfig) => {
        setSelectedConfig(cfg);
        setShowEditModal(true);
    };

    const handleConfigDeleted = (deletedId: number) => {
        setConfigs(prev => prev.filter(cfg => cfg.id !== deletedId));
        setShowDeleteModal(false);
        setDeleteConfig(null);
    };

    const handleDeleteClick = (cfg: CodigoSesionPosConfig) => {
        setDeleteConfig(cfg);
        setShowDeleteModal(true);
    };

    if (perm === null || (loading && configs.length === 0)) {
        return <Loading text="Cargando sesiones pos..." />;
    }

    return <><LumaResourceWorkspace title="Código de sesión POS" description="Consulta y gestiona la información de tu coworking." group="Configuraciones" rows={configs} columns={[{key:'nombre',label:'Nombre'},{key:'prefijo',label:'Prefijo'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowAddModal(true)}:undefined} onEdit={perm?.can_update?handleEdit:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{showAddModal && (<AddCodigoSesionPos
                        onClose={() => setShowAddModal(false)}
                        onCreated={handleConfigCreated}
                    />)}{showEditModal && selectedConfig && (<EditCodigoSesionPos
                        config={selectedConfig}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedConfig(null);
                        }}
                        onUpdated={handleConfigUpdated}
                    />)}{showDeleteModal && deleteConfig && (<DeleteCodigoSesionPos
                        id={deleteConfig.id}
                        prefijo={deleteConfig?.prefijo ?? ''}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setDeleteConfig(null);
                        }}
                        onDeleted={handleConfigDeleted}
                    />)}</>;
};

export default CodigoSesionPosPage;
